import "../styles/globals.css"

import client, { setInitialSession } from "@/api/client"
import { Layout } from "@/components/Layout"
import DefaultHead from "@/components/Layout/DefaultHead"
import { AccountProvider } from "@/contexts/account-context"
import { WorkspaceProvider } from "@/contexts/workspace-context"
import useWorkspaceContext from "@/hooks/useWorkspaceContext"
import { customQueryClient } from "@/utils/queries"
import { buildBrandingTheme } from "@/utils/theme"
import { fetchCurrentWorkspaceBranding } from "@/utils/workspaceBranding"
import { resolveWorkspaceHost } from "@/utils/workspaceHost"
import { WorkspaceBranding } from "@/utils/workspaceMemberships"
import { ChakraProvider, Flex } from "@chakra-ui/react"
import { Session } from "next-auth"
import { SessionProvider } from "next-auth/react"
import { appWithTranslation, SSRConfig } from "next-i18next"
import NextApp, { AppContext, AppProps } from "next/app"
import { useEffect, useMemo, useRef } from "react"
import { QueryClientProvider } from "react-query"

type AppPageProps = {
  session?: Session
  workspace?: WorkspaceBranding | null
}

const ThemedApp = ({
  Component,
  pageProps,
}: {
  Component: AppProps["Component"]
  pageProps: any
}) => {
  const { current } = useWorkspaceContext()
  const theme = useMemo(
    () => buildBrandingTheme(current?.primary_color),
    [current?.primary_color]
  )

  useEffect(() => {
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({
        brandColor: theme.colors.primaryColor,
        brandTextColor: theme.colors.primaryColorText,
      })
    )
  }, [theme])

  // Pages that opt out of the app chrome (ToolBar + NavigationBar), e.g. the
  // workspace join screen shown to a logged-in user who isn't a member yet.
  const isStandalone = (Component as { standalone?: boolean }).standalone
  const isMarketing = (pageProps as { marketing?: boolean })?.marketing

  return (
    <ChakraProvider theme={theme}>
      <AccountProvider>
        {isStandalone ? (
          <Flex
            bg="secondary.light"
            minHeight="100vh"
            align="center"
            justify="center"
            p={4}
          >
            <DefaultHead />
            <Component {...pageProps} />
          </Flex>
        ) : (
          <Layout marketing={isMarketing}>
            <DefaultHead />
            <Component {...pageProps} />
          </Layout>
        )}
      </AccountProvider>
    </ChakraProvider>
  )
}

function App({
  Component,
  pageProps: { session, workspace, ...pageProps },
}: AppProps<AppPageProps>) {
  if (session) {
    setInitialSession(session)
  }

  useEffect(() => {
    window.addDevice = (uuid: string, token: string) => {
      client.devices.postDevices({ data: { uuid, token } })
    }
  }, [])

  return (
    <SessionProvider session={session}>
      <WorkspaceProvider initialCurrent={workspace}>
        <QueryClientProvider client={customQueryClient}>
          <ThemedApp Component={Component} pageProps={pageProps} />
        </QueryClientProvider>
      </WorkspaceProvider>
    </SessionProvider>
  )
}

function subdomainFromAppContext(appContext: AppContext) {
  const req = appContext.ctx.req

  if (!req) {
    return undefined
  }

  const header = req.headers["x-resolved-workspace-subdomain"]

  if (typeof header === "string" && header) {
    return header
  }

  const host = req.headers.host
  const rootDomain = process.env.NEXT_PUBLIC_ROOT_DOMAIN as string

  if (!host || !rootDomain) {
    return undefined
  }

  const resolution = resolveWorkspaceHost(host, rootDomain)

  if (resolution.kind === "workspace") {
    return resolution.subdomain
  }

  return undefined
}

App.getInitialProps = async (appContext: AppContext) => {
  const appProps = await NextApp.getInitialProps(appContext)

  if (typeof window !== "undefined") {
    return appProps
  }

  const workspace = await fetchCurrentWorkspaceBranding(
    subdomainFromAppContext(appContext)
  )

  return {
    ...appProps,
    pageProps: {
      ...appProps.pageProps,
      workspace,
    },
  }
}

const AppWithTranslation = appWithTranslation(App)

// With a middleware, Next refetches a missing page's data after hydration and
// gets `{}`: the 404 then lost its translations, and the layout's with it
const AppKeepingTranslations = (props: AppProps<AppPageProps & SSRConfig>) => {
  const translations = useRef(props.pageProps._nextI18Next)
  translations.current = props.pageProps._nextI18Next ?? translations.current

  return (
    <AppWithTranslation
      {...props}
      pageProps={{ ...props.pageProps, _nextI18Next: translations.current }}
    />
  )
}

AppKeepingTranslations.getInitialProps = App.getInitialProps

export default AppKeepingTranslations
